import React, { useLayoutEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { ArrowLeft, CheckCircle, WarningCircle } from 'phosphor-react';

export type EstadoDoSalvamento = 'salvando' | 'erro' | 'salvo' | 'nunca';

interface BarraDoWizardProps {
  /** Coluna do conteúdo: a barra acompanha a largura dela. */
  colunaRef: React.RefObject<HTMLElement>;
  salvamento: EstadoDoSalvamento;
  salvoEm: Date | null;
  onTentarSalvar: () => void;
  podeVoltar: boolean;
  onEtapaAnterior: () => void;
  onSalvarESair: () => void;
  salvandoESaindo: boolean;
  /** Ações da etapa: "Continuar", ou "Preview" + "Gerar contrato" na Revisão. */
  children: React.ReactNode;
}

const horaDoSalvamento = (d: Date): string => {
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const hoje = new Date();
  const mesmoDia =
    d.getDate() === hoje.getDate() &&
    d.getMonth() === hoje.getMonth() &&
    d.getFullYear() === hoje.getFullYear();
  return mesmoDia ? `Salvo às ${hora}` : `Salvo em ${d.toLocaleDateString('pt-BR')} ${hora}`;
};

/**
 * Posição horizontal da coluna do conteúdo na tela. Medida no próprio elemento (e não
 * pela largura do menu lateral, que é estado interno do Layout): acompanha o menu
 * abrir/fechar e a janela mudar de tamanho.
 */
const useLimitesDaColuna = (ref: React.RefObject<HTMLElement>) => {
  const [limites, setLimites] = useState<{ left: number; right: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const medir = () => {
      const r = el.getBoundingClientRect();
      // clientWidth, não innerWidth: o `right` do fixed conta da borda da barra de rolagem.
      const larguraUtil = document.documentElement.clientWidth;
      setLimites({ left: Math.max(r.left, 0), right: Math.max(larguraUtil - r.right, 0) });
    };
    medir();
    const observador = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    observador?.observe(el);
    // Em tela larga a coluna tem largura máxima: abrir/fechar o menu só a desloca, sem
    // mudar o tamanho dela. Quem muda de tamanho é o pai.
    if (el.parentElement) observador?.observe(el.parentElement);
    window.addEventListener('resize', medir);
    return () => {
      observador?.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [ref]);
  return limites;
};

/**
 * Barra de ações do wizard que acompanha a rolagem.
 *
 * ⚠️ `position: fixed`, NUNCA `sticky`: neste admin quem rola é o documento e o
 * `sticky` fica inerte em silêncio (#root sem altura; e o passo mora num GlassCard
 * com overflow hidden). Mesmo arranjo da `DockContrato` dos eventos.
 *
 * O estado do salvamento é TEXTO sempre visível: o wizard salva a cada Continuar, e o
 * operador precisa ver que salvou (e se falhou) antes de sair.
 */
export const BarraDoWizard: React.FC<BarraDoWizardProps> = ({
  colunaRef,
  salvamento,
  salvoEm,
  onTentarSalvar,
  podeVoltar,
  onEtapaAnterior,
  onSalvarESair,
  salvandoESaindo,
  children,
}) => {
  const limites = useLimitesDaColuna(colunaRef);

  let status: React.ReactNode = null;
  if (salvamento === 'salvando') {
    status = (
      <Flex align="center" gap={1.5} color="gray.500">
        <Spinner size="xs" />
        <Text fontSize="12px">Salvando…</Text>
      </Flex>
    );
  } else if (salvamento === 'erro') {
    status = (
      <Flex align="center" gap={1.5} color="red.600">
        <WarningCircle size={14} weight="fill" />
        <Text fontSize="12px" fontWeight={600}>
          Não salvou.
        </Text>
        <Button size="xs" variant="link" colorScheme="red" onClick={onTentarSalvar}>
          Tentar de novo
        </Button>
      </Flex>
    );
  } else if (salvamento === 'salvo' && salvoEm) {
    status = (
      <Flex align="center" gap={1.5} color="green.600">
        <CheckCircle size={14} weight="fill" />
        <Text fontSize="12px">{horaDoSalvamento(salvoEm)}</Text>
      </Flex>
    );
  }

  return (
    <Box
      position="fixed"
      bottom={4}
      left={limites ? `${limites.left}px` : 4}
      right={limites ? `${limites.right}px` : 4}
      zIndex={20}
      bg="white"
      borderRadius="12px"
      boxShadow="0 10px 30px -10px rgba(15, 23, 42, 0.28)"
      border="1px solid"
      borderColor="gray.100"
      px={4}
      py={3}
      data-testid="barra-do-wizard"
    >
      {/* No celular, duas linhas em vez de quatro: a seta (só o ícone), o estado e o
          "Salvar e sair" em cima; as ações da etapa lado a lado embaixo. Empilhada, a
          barra cobria um terço da tela. */}
      <Flex align="center" gap={3} wrap="wrap">
        <Button
          variant="outline"
          aria-label="Etapa anterior"
          leftIcon={<ArrowLeft size={16} weight="bold" />}
          iconSpacing={{ base: 0, md: 2 }}
          px={{ base: 3, md: 10 }}
          onClick={onEtapaAnterior}
          isDisabled={!podeVoltar}
        >
          <Box as="span" display={{ base: 'none', md: 'inline' }}>
            Etapa anterior
          </Box>
        </Button>
        <Box flex={1} />
        {status}
        <Tooltip
          label="Salva como rascunho e volta à lista de contratos. Outro usuário do departamento pode retomar de onde você parou."
          placement="top"
          hasArrow
          openDelay={300}
        >
          <Button
            variant="ghost"
            colorScheme="gray"
            px={{ base: 2, md: 10 }}
            onClick={onSalvarESair}
            isLoading={salvandoESaindo}
          >
            Salvar e sair
          </Button>
        </Tooltip>
        <Flex
          gap={3}
          flexBasis={{ base: '100%', md: 'auto' }}
          // No celular, recuo menor: com os 40 px do tema, "Gerar contrato" passava da borda.
          sx={{ '& > button': { flex: { base: 1, md: 'initial' }, px: { base: 3, md: 10 } } }}
        >
          {children}
        </Flex>
      </Flex>
    </Box>
  );
};

export default BarraDoWizard;
